import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getLoggedInKey, getLoggedInRoleKey } from '../utils/browserStorage.util';

type RoleGuardProps = {
  allowedRoles: string[];
};

const RoleGuard = ({ allowedRoles }: RoleGuardProps) => {
  const loggedIn = getLoggedInKey();
  const role = getLoggedInRoleKey();
  const location = useLocation();

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log("RoleGuard check:", { loggedIn, role, allowedRoles, pathname: location.pathname });
  }

  if (!loggedIn || !role) {
    if (process.env.NODE_ENV === 'development') {
      console.log("Not logged in or no role, redirecting to login");
    }
    return <Navigate to="/login" />;
  }
  
  if (!allowedRoles.includes(role)) {
      // Always redirect employees to statistics instead of unauthorized
      if (role === "employee") {
        // Only redirect if not already on a statistics page to prevent loops
        if (location.pathname !== "/statistics" && location.pathname !== "/graphical-statistics") {
          if (process.env.NODE_ENV === 'development') {
            console.log("Employee trying to access admin route, redirecting to /statistics");
          }
          return <Navigate to="/statistics" replace />;
        }
        // Already on statistics page, allow it
        return <Outlet />;
      }
      // If admin hits employee guard, allow access to statistics pages (admins can see them too)
      // For other routes, redirect to statistics (which will then match admin guard)
      if (role === "admin") {
        // Admins can access statistics pages through employee routes
        if (location.pathname === "/statistics" || location.pathname === "/graphical-statistics") {
          if (process.env.NODE_ENV === 'development') {
            console.log("Admin accessing employee route, allowing it");
          }
          return <Outlet />;
        }
        // Admin on non-statistics route hitting employee guard - redirect to statistics
        // This will then match the admin guard which has all routes
        if (process.env.NODE_ENV === 'development') {
          console.log("Admin hit employee guard, redirecting to /statistics");
        }
        // Only redirect if not already on statistics to prevent loops
        if (location.pathname !== "/statistics") {
          return <Navigate to="/statistics" replace />;
        }
        // Already on statistics, allow it
        return <Outlet />;
      }
      // For other invalid roles, redirect to unauthorized
      if (process.env.NODE_ENV === 'development') {
        console.log("Invalid role, redirecting to /unauthorized");
      }
      return <Navigate to="/unauthorized" replace />;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log("Role guard passed, rendering outlet");
  }
  return <Outlet />;
};

export default RoleGuard;
